import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Grid2 from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import TennisCourt from '../tennis-court';
import lodash from 'lodash';
import Button from '@mui/material/Button';
import {
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  buttonStyles,
  steps,
} from './constants';
import StepperContent from './stepper-content';

function TrackPlayerLocation({ socket, deviceId }) {
  const [playerLocation, setPlayerLocation] = useState(0);
  const [sessionId, setSessionId] = useState('');
  const [showStepper, setShowStepper] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedLocationBlock, setSelectedLocationBlock] = useState(null);
  const [selectedShotType, setSelectedShotType] = useState(null);
  const [selectedSpinLevel, setSelectedSpinLevel] = useState('');

  const resetStepper = () => {
    setActiveStep(0);
    setSelectedLocationBlock(null);
    setSelectedShotType(null);
    setSelectedSpinLevel(null);
  };

  const handleSubmit = async () => {
    const payload = {
      deviceId,
      ballDeliveryPattern: `BLOCK_${selectedLocationBlock}_${selectedShotType}_${selectedSpinLevel}`,
      timestamp: dayjs.utc().unix()
    };
    await socket.emit('send_forced_next_delivery_pattern', payload);
    resetStepper();
    setShowStepper(false);
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  const handleSelectLocationBlock = (locationId) => {
    setSelectedLocationBlock(locationId);
    handleNext();
  };

  const handleSelectShotType = (shotType) => {
    setSelectedShotType(shotType);
    handleNext();
  };

  const handleSelectSpinLevel = (spinLevel = 'L5') => {
    setSelectedSpinLevel(spinLevel);
  };

  const toggleDrawer = (event, value) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }

    if (value === false) {
      resetStepper();
    }

    setShowStepper(value);
  };

  const receivedPlayerLocation = (data) => {
    try {
      const {
        current_location_id: currentLocationId = '0',
        current_session_id: currentSessionId
      } = data;
      if (deviceId === lodash.get(data, 'deviceId', '') && currentLocationId !== '0') {
        setPlayerLocation(Number(currentLocationId));
        setSessionId(currentSessionId);
      }
    } catch (err) {
      console.error('receivedPlayerLocation error:', err);
    }
  };

  useEffect(() => {
    if (socket) {
      socket.on('track:player_location', receivedPlayerLocation);

      return () => {
        socket.off('track:player_location', receivedPlayerLocation);
      };
    }
  }, [socket]);

  return (
    <Grid2 container spacing={2}>
      <Grid2 item size={12}>
        <Stack direction="row" spacing={2} justifyContent="space-evenly">
          <Box width="40%">
            {
              playerLocation === '0' ? <Typography>{'Player is outsize the playing zone'}</Typography>
                : null
            }
          </Box>
          <Box width="40%"><Typography>{`Session Id: ${sessionId}`}</Typography></Box>
          <Box width="20%">
            <Button variant="contained" color="secondary" onClick={(e) => setShowStepper(e, true)}>
              {'Keyboard Player Mode'}
            </Button>
          </Box>
        </Stack>
      </Grid2>
      <Grid2 item size={12}>
        <Typography variant="h6">Track Player Location</Typography>
      </Grid2>
      <Grid2 item size={12}>
        <TennisCourt activeBlock={playerLocation}/>
      </Grid2>
      <Grid2 item size={12}>
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', marginY: '2rem' }}>
          <Drawer
            anchor="top"
            open={showStepper}
            onClose={(e) => toggleDrawer(e, false)}
          >
            <Box width="100%" minHeight="400px" height="auto" paddingY="2rem" role="presentation">
              <Box sx={{ width: '70%', display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                {`Player Location: ${playerLocation}`}
              </Box>
              <Box sx={{ width: '100%', marginTop: '2rem' }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>

                <StepperContent
                  step={activeStep}
                  playerLocation={playerLocation}
                  handleSelectLocationBlock={handleSelectLocationBlock}
                  selectedLocationBlock={selectedLocationBlock}
                  handleSelectShotType={handleSelectShotType}
                  selectedShotType={selectedShotType}
                  handleSelectSpinLevel={handleSelectSpinLevel}
                  selectedSpinLevel={selectedSpinLevel}
                />

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 4,
                    gap: 2,
                  }}
                >
                  {activeStep > 0 && (
                    <Button onClick={handleBack} sx={{ backgroundColor: '#4A4A4A', color: '#FFF' }}>
                      Back
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={buttonStyles}
                    disabled={
                      (steps[activeStep] === 'Select Location Block' && !selectedLocationBlock) ||
                      (steps[activeStep] === 'Shot Type' && !selectedShotType) ||
                      (steps[activeStep] === 'Spin Level' && !selectedSpinLevel)
                    }
                  >
                    {activeStep === steps.length - 1 ? 'Submit' : 'Next'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Drawer>
        </Box>
      </Grid2>
    </Grid2>
  );
}

TrackPlayerLocation.propTypes = {
  socket: PropTypes.object.isRequired,
  deviceId: PropTypes.string.isRequired,
};

export default React.memo(TrackPlayerLocation);
